import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  IconButton,
  Button,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Skeleton,
  Collapse,
  alpha,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Calendar,
  Target,
  AlertTriangle,
  Bug,
  Lightbulb,
  Zap,
  HelpCircle,
  FileText,
  Layers,
  Lock,
  Link2,
} from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import { Task, TaskStatus, TaskPriority, TaskCategory, IssueType, Project, Milestone } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';

dayjs.extend(isBetween);

// View mode for the timeline
type ViewMode = 'day' | 'week' | 'month';

interface ProjectTasksGanttChartProps {
  project: Project;
  tasks: Task[];
  milestones?: Milestone[];
  isLoading?: boolean;
  onTaskClick?: (taskId: string) => void;
}

// Status colors
const statusColors: Record<TaskStatus, string> = {
  [TaskStatus.NOT_STARTED]: '#64748b',
  [TaskStatus.IN_PROGRESS]: '#2563eb',
  [TaskStatus.BLOCKED]: '#f59e0b',
  [TaskStatus.COMPLETED]: '#16a34a',
  [TaskStatus.CANCELLED]: '#ef4444',
};

// Priority colors
const priorityColors: Record<TaskPriority, string> = {
  [TaskPriority.P1]: '#ef4444',
  [TaskPriority.P2]: '#f59e0b',
  [TaskPriority.P3]: '#3b82f6',
  [TaskPriority.P4]: '#64748b',
};

// Category colors
const categoryColors: Record<TaskCategory, string> = {
  task: '#1e40af',
  subtask: '#0369a1',
  development: '#2563eb',
  design: '#8b5cf6',
  testing: '#16a34a',
  documentation: '#0891b2',
  research: '#7c3aed',
  meeting: '#f59e0b',
  review: '#ec4899',
  support: '#14b8a6',
  maintenance: '#6366f1',
  deployment: '#ef4444',
  planning: '#64748b',
  other: '#9ca3af',
};

// Issue type config
const issueTypeConfig: Record<IssueType, { icon: React.FC<any>; color: string; label: string }> = {
  bug: { icon: Bug, color: '#dc2626', label: 'Bug' },
  feature: { icon: Lightbulb, color: '#16a34a', label: 'Feature' },
  improvement: { icon: Zap, color: '#2563eb', label: 'Improvement' },
  task: { icon: FileText, color: '#6b7280', label: 'Task' },
  question: { icon: HelpCircle, color: '#9333ea', label: 'Question' },
};

// Row height for tasks
const ROW_HEIGHT = 44;
const HEADER_HEIGHT = 64;

/**
 * ProjectTasksGanttChart
 * Timeline view showing tasks with graphical dependency arrows
 */
const ProjectTasksGanttChart: React.FC<ProjectTasksGanttChartProps> = ({
  project,
  tasks,
  milestones = [],
  isLoading = false,
  onTaskClick,
}) => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('day');
  const [startDate, setStartDate] = useState(() => {
    // Start from project start date or today
    return project.startDate ? dayjs(project.startDate).startOf('week') : dayjs().startOf('week');
  });
  const [showSubtasks, setShowSubtasks] = useState(true);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());

  // Calculate column width based on view mode
  const columnWidth = viewMode === 'day' ? 40 : viewMode === 'week' ? 60 : 100;
  const totalDays = viewMode === 'day' ? 30 : viewMode === 'week' ? 56 : 120;

  // Generate date range
  const dateRange = useMemo(() => {
    const dates: Dayjs[] = [];
    let current = startDate;
    const end = startDate.add(totalDays, 'day');

    while (current.isBefore(end)) {
      dates.push(current);
      if (viewMode === 'day') {
        current = current.add(1, 'day');
      } else if (viewMode === 'week') {
        current = current.add(1, 'week');
      } else {
        current = current.add(1, 'month');
      }
    }
    return dates;
  }, [startDate, viewMode, totalDays]);

  // Build task hierarchy with parent-child relationships
  const taskHierarchy = useMemo(() => {
    // Get root tasks (no parent)
    const rootTasks = tasks.filter((t) => !t.parentTaskId).sort((a, b) => {
      // Sort by start date, then by order
      const startDiff = dayjs(a.startDate || a.dueDate).diff(dayjs(b.startDate || b.dueDate));
      return startDiff !== 0 ? startDiff : a.order - b.order;
    });

    // Build flattened list with hierarchy info
    const flattenedTasks: { task: Task; level: number; parentId?: string }[] = [];

    const addTaskWithChildren = (task: Task, level: number) => {
      flattenedTasks.push({ task, level, parentId: task.parentTaskId });

      if (showSubtasks && expandedTasks.has(task.id)) {
        // Get subtasks
        const subtasks = tasks
          .filter((t) => t.parentTaskId === task.id)
          .sort((a, b) => a.order - b.order);
        subtasks.forEach((subtask) => addTaskWithChildren(subtask, level + 1));
      }
    };

    rootTasks.forEach((task) => addTaskWithChildren(task, 0));
    return flattenedTasks;
  }, [tasks, showSubtasks, expandedTasks]);

  // Create task lookup for quick access
  const taskLookup = useMemo(() => {
    const map = new Map<string, Task>();
    tasks.forEach((t) => map.set(t.id, t));
    return map;
  }, [tasks]);

  // Calculate bar position for a task
  const getBarPosition = (task: Task) => {
    const taskStart = dayjs(task.startDate || task.dueDate);
    const taskEnd = dayjs(task.dueDate);
    const rangeStart = startDate;
    const rangeEnd = startDate.add(totalDays, 'day');

    // Check if task is in range
    if (taskEnd.isBefore(rangeStart) || taskStart.isAfter(rangeEnd)) {
      return null;
    }

    const effectiveStart = taskStart.isBefore(rangeStart) ? rangeStart : taskStart;
    const effectiveEnd = taskEnd.isAfter(rangeEnd) ? rangeEnd : taskEnd;

    const left = effectiveStart.diff(rangeStart, 'day') * columnWidth;
    const width = Math.max(effectiveEnd.diff(effectiveStart, 'day') * columnWidth, 20);

    return { left, width };
  };

  // Calculate milestone position
  const getMilestonePosition = (milestone: Milestone) => {
    const milestoneDate = dayjs(milestone.dueDate);
    const rangeStart = startDate;
    const rangeEnd = startDate.add(totalDays, 'day');

    if (milestoneDate.isBefore(rangeStart) || milestoneDate.isAfter(rangeEnd)) {
      return null;
    }

    const left = milestoneDate.diff(rangeStart, 'day') * columnWidth;
    return left;
  };

  // Calculate dependency arrow paths - Flowchart style with clear orthogonal connections
  const getDependencyArrows = useMemo(() => {
    const arrows: {
      id: string;
      fromTask: Task;
      toTask: Task;
      path: string;
      fromY: number;
      toY: number;
      fromX: number;
      toX: number;
      type: 'horizontal' | 'vertical-down' | 'vertical-up' | 'diagonal';
    }[] = [];

    const CONNECTOR_OFFSET = 15; // Gap between bar end and arrow start
    const CORNER_RADIUS = 8; // Rounded corner radius

    taskHierarchy.forEach((item, toIndex) => {
      const toTask = item.task;
      const blockedBy = toTask.blockedByTaskIds || [];

      blockedBy.forEach((fromId) => {
        const fromTask = taskLookup.get(fromId);
        if (!fromTask) return;

        // Find position of fromTask in hierarchy
        const fromIndex = taskHierarchy.findIndex((i) => i.task.id === fromId);
        if (fromIndex === -1) return;

        const fromPos = getBarPosition(fromTask);
        const toPos = getBarPosition(toTask);
        if (!fromPos || !toPos) return;

        // Calculate Y positions (center of rows)
        const fromY = fromIndex * ROW_HEIGHT + ROW_HEIGHT / 2;
        const toY = toIndex * ROW_HEIGHT + ROW_HEIGHT / 2;

        // Calculate X positions
        const fromX = fromPos.left + fromPos.width + CONNECTOR_OFFSET;
        const toX = toPos.left - CONNECTOR_OFFSET;

        // Determine arrow type based on relative positions
        const isGoingDown = toY > fromY;
        const isGoingUp = toY < fromY;
        const isSameRow = Math.abs(toY - fromY) < ROW_HEIGHT / 2;

        let path: string;
        let type: 'horizontal' | 'vertical-down' | 'vertical-up' | 'diagonal';

        if (isSameRow) {
          // Horizontal connection - straight line
          path = `M ${fromX - CONNECTOR_OFFSET} ${fromY} L ${toX + CONNECTOR_OFFSET} ${toY}`;
          type = 'horizontal';
        } else if (toX > fromX) {
          // Target is to the right - use right-angle path with rounded corners
          const midX = fromX + (toX - fromX) / 2;
          
          if (isGoingDown) {
            // Path goes right then down then right
            path = `M ${fromX - CONNECTOR_OFFSET} ${fromY} 
                    L ${midX - CORNER_RADIUS} ${fromY} 
                    Q ${midX} ${fromY}, ${midX} ${fromY + CORNER_RADIUS}
                    L ${midX} ${toY - CORNER_RADIUS}
                    Q ${midX} ${toY}, ${midX + CORNER_RADIUS} ${toY}
                    L ${toX + CONNECTOR_OFFSET} ${toY}`;
            type = 'vertical-down';
          } else {
            // Path goes right then up then right
            path = `M ${fromX - CONNECTOR_OFFSET} ${fromY} 
                    L ${midX - CORNER_RADIUS} ${fromY} 
                    Q ${midX} ${fromY}, ${midX} ${fromY - CORNER_RADIUS}
                    L ${midX} ${toY + CORNER_RADIUS}
                    Q ${midX} ${toY}, ${midX + CORNER_RADIUS} ${toY}
                    L ${toX + CONNECTOR_OFFSET} ${toY}`;
            type = 'vertical-up';
          }
        } else {
          // Target is to the left or overlapping - use S-curve path
          const offsetX = 30; // How far to go right before curving
          
          if (isGoingDown) {
            path = `M ${fromX - CONNECTOR_OFFSET} ${fromY}
                    L ${fromX + offsetX - CORNER_RADIUS} ${fromY}
                    Q ${fromX + offsetX} ${fromY}, ${fromX + offsetX} ${fromY + CORNER_RADIUS}
                    L ${fromX + offsetX} ${toY - CORNER_RADIUS}
                    Q ${fromX + offsetX} ${toY}, ${fromX + offsetX - CORNER_RADIUS} ${toY}
                    L ${toX + CONNECTOR_OFFSET} ${toY}`;
            type = 'vertical-down';
          } else {
            path = `M ${fromX - CONNECTOR_OFFSET} ${fromY}
                    L ${fromX + offsetX - CORNER_RADIUS} ${fromY}
                    Q ${fromX + offsetX} ${fromY}, ${fromX + offsetX} ${fromY - CORNER_RADIUS}
                    L ${fromX + offsetX} ${toY + CORNER_RADIUS}
                    Q ${fromX + offsetX} ${toY}, ${fromX + offsetX - CORNER_RADIUS} ${toY}
                    L ${toX + CONNECTOR_OFFSET} ${toY}`;
            type = 'vertical-up';
          }
        }

        arrows.push({
          id: `${fromId}-${toTask.id}`,
          fromTask,
          toTask,
          path,
          fromY,
          toY,
          fromX,
          toX,
          type,
        });
      });
    });

    return arrows;
  }, [taskHierarchy, taskLookup, columnWidth, startDate, totalDays]);

  // Navigation handlers
  const handlePrevious = () => {
    setStartDate((d) => d.subtract(viewMode === 'day' ? 7 : viewMode === 'week' ? 14 : 30, 'day'));
  };

  const handleNext = () => {
    setStartDate((d) => d.add(viewMode === 'day' ? 7 : viewMode === 'week' ? 14 : 30, 'day'));
  };

  const handleToday = () => {
    setStartDate(dayjs().startOf('week'));
  };

  const handleTaskClick = (taskId: string) => {
    if (onTaskClick) {
      onTaskClick(taskId);
    } else {
      navigate(`/tasks?taskId=${taskId}`);
    }
  };

  const toggleTaskExpand = (taskId: string) => {
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  // Group dates for header
  const getGroupedDates = () => {
    const groups: { label: string; span: number; dates: Dayjs[] }[] = [];
    let currentGroup: { label: string; span: number; dates: Dayjs[] } | null = null;

    dateRange.forEach((date) => {
      let label: string;
      switch (viewMode) {
        case 'day':
          label = date.format('MMM YYYY');
          break;
        case 'week':
          label = date.format('MMM YYYY');
          break;
        case 'month':
          label = date.format('YYYY');
          break;
        default:
          label = date.format('MMM YYYY');
      }

      if (!currentGroup || currentGroup.label !== label) {
        currentGroup = { label, span: 1, dates: [date] };
        groups.push(currentGroup);
      } else {
        currentGroup.span++;
        currentGroup.dates.push(date);
      }
    });

    return groups;
  };

  const groupedDates = getGroupedDates();

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevious} size="small">
            <ChevronLeft size={20} />
          </IconButton>
          <Button variant="outlined" size="small" onClick={handleToday}>
            Today
          </Button>
          <IconButton onClick={handleNext} size="small">
            <ChevronRight size={20} />
          </IconButton>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {startDate.format('MMM YYYY')} -{' '}
            {startDate.add(totalDays, 'day').format('MMM YYYY')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Show/Hide Subtasks */}
          <Button
            size="small"
            variant={showSubtasks ? 'contained' : 'outlined'}
            startIcon={<Layers size={14} />}
            onClick={() => setShowSubtasks(!showSubtasks)}
            sx={{ textTransform: 'none' }}
          >
            Subtasks
          </Button>

          {/* View Mode */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as ViewMode)}
            >
              <MenuItem value="day">Days</MenuItem>
              <MenuItem value="week">Weeks</MenuItem>
              <MenuItem value="month">Months</MenuItem>
            </Select>
          </FormControl>

          {/* Legend */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: statusColors[TaskStatus.COMPLETED],
                }}
              />
              <Typography variant="caption">Completed</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: statusColors[TaskStatus.IN_PROGRESS],
                }}
              />
              <Typography variant="caption">In Progress</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: statusColors[TaskStatus.BLOCKED],
                }}
              />
              <Typography variant="caption">Blocked</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Target size={12} style={{ color: '#9333ea' }} />
              <Typography variant="caption">Milestone</Typography>
            </Box>
            {/* Dependency Line Legend */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}>
              <svg width="24" height="12" viewBox="0 0 24 12">
                <line x1="0" y1="6" x2="20" y2="6" stroke="#9333ea" strokeWidth="2" />
                <polygon points="20,3 24,6 20,9" fill="#9333ea" />
              </svg>
              <Typography variant="caption">Active</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <svg width="24" height="12" viewBox="0 0 24 12">
                <line x1="0" y1="6" x2="20" y2="6" stroke="#16a34a" strokeWidth="2" />
                <polygon points="20,3 24,6 20,9" fill="#16a34a" />
              </svg>
              <Typography variant="caption">Resolved</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <svg width="24" height="12" viewBox="0 0 24 12">
                <line x1="0" y1="6" x2="20" y2="6" stroke="#f59e0b" strokeWidth="2" strokeDasharray="4,2" />
                <polygon points="20,3 24,6 20,9" fill="#f59e0b" />
              </svg>
              <Typography variant="caption">Blocking</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Gantt Content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Task List (Fixed) */}
        <Box
          sx={{
            width: 320,
            minWidth: 320,
            borderRight: '1px solid',
            borderColor: 'divider',
            overflow: 'auto',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              height: HEADER_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              px: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              Tasks ({tasks.length})
            </Typography>
          </Box>

          {/* Task Rows */}
          {taskHierarchy.map(({ task, level }) => {
            const hasSubtasks = tasks.some((t) => t.parentTaskId === task.id);
            const isExpanded = expandedTasks.has(task.id);
            const isBlocked = (task.blockedByTaskIds?.length || 0) > 0;
            const IssueIcon = task.issueType ? issueTypeConfig[task.issueType]?.icon : null;

            return (
              <Box
                key={task.id}
                onClick={() => handleTaskClick(task.id)}
                sx={{
                  height: ROW_HEIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  pl: 1 + level * 2,
                  gap: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  bgcolor: isBlocked ? alpha('#f59e0b', 0.05) : 'transparent',
                  '&:hover': {
                    bgcolor: isBlocked ? alpha('#f59e0b', 0.1) : 'grey.50',
                  },
                }}
              >
                {/* Expand/Collapse for tasks with subtasks */}
                {hasSubtasks && showSubtasks ? (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTaskExpand(task.id);
                    }}
                    sx={{ p: 0.25 }}
                  >
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </IconButton>
                ) : (
                  <Box sx={{ width: 22 }} />
                )}

                {/* Task Icon */}
                {hasSubtasks ? (
                  <Layers size={14} style={{ color: '#8b5cf6' }} />
                ) : IssueIcon ? (
                  <IssueIcon
                    size={14}
                    style={{ color: issueTypeConfig[task.issueType!].color }}
                  />
                ) : (
                  <FileText size={14} style={{ color: '#64748b' }} />
                )}

                {/* Task Title */}
                <Tooltip title={task.title}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      flex: 1,
                      fontWeight: level === 0 ? 500 : 400,
                      textDecoration:
                        task.status === TaskStatus.COMPLETED ? 'line-through' : 'none',
                      color:
                        task.status === TaskStatus.COMPLETED
                          ? 'text.secondary'
                          : 'text.primary',
                    }}
                  >
                    {task.title}
                  </Typography>
                </Tooltip>

                {/* Category */}
                {task.category && (
                  <Chip
                    label={task.category.slice(0, 3)}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: '0.55rem',
                      bgcolor: alpha(categoryColors[task.category] || '#9ca3af', 0.15),
                      color: categoryColors[task.category] || '#9ca3af',
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                )}

                {/* Blocked indicator */}
                {isBlocked && (
                  <Tooltip title={`Blocked by ${task.blockedByTaskIds?.length} task(s)`}>
                    <Lock size={12} style={{ color: '#f59e0b' }} />
                  </Tooltip>
                )}

                {/* Priority */}
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: priorityColors[task.priority],
                  }}
                />
              </Box>
            );
          })}

          {taskHierarchy.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No tasks to display
              </Typography>
            </Box>
          )}
        </Box>

        {/* Timeline (Scrollable) */}
        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            overflow: 'auto',
            position: 'relative',
          }}
        >
          {/* Date Headers */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              bgcolor: 'background.paper',
            }}
          >
            {/* Month/Year Row */}
            <Box
              sx={{
                display: 'flex',
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              {groupedDates.map((group, idx) => (
                <Box
                  key={idx}
                  sx={{
                    width: group.span * columnWidth,
                    minWidth: group.span * columnWidth,
                    px: 1,
                    py: 0.5,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" fontWeight={600}>
                    {group.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Day Row */}
            <Box
              sx={{
                display: 'flex',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              {dateRange.map((date, idx) => {
                const isToday = date.isSame(dayjs(), 'day');
                const isWeekend = date.day() === 0 || date.day() === 6;

                return (
                  <Box
                    key={idx}
                    sx={{
                      width: columnWidth,
                      minWidth: columnWidth,
                      py: 0.5,
                      textAlign: 'center',
                      bgcolor: isToday
                        ? 'primary.50'
                        : isWeekend
                        ? 'grey.100'
                        : 'transparent',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: viewMode === 'month' ? '0.6rem' : '0.7rem',
                        fontWeight: isToday ? 600 : 400,
                        color: isToday ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      {viewMode === 'day'
                        ? date.format('D')
                        : viewMode === 'week'
                        ? date.format('D')
                        : date.date() === 1
                        ? date.format('MMM')
                        : ''}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Gantt Bars */}
          <Box sx={{ position: 'relative' }}>
            {/* Grid Lines */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                pointerEvents: 'none',
              }}
            >
              {dateRange.map((date, idx) => {
                const isToday = date.isSame(dayjs(), 'day');
                const isWeekend = date.day() === 0 || date.day() === 6;

                return (
                  <Box
                    key={idx}
                    sx={{
                      width: columnWidth,
                      minWidth: columnWidth,
                      bgcolor: isToday
                        ? 'rgba(37, 99, 235, 0.1)'
                        : isWeekend
                        ? 'rgba(0,0,0,0.02)'
                        : 'transparent',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                );
              })}
            </Box>

            {/* Today Line */}
            {dayjs().isBetween(startDate, startDate.add(totalDays, 'day'), null, '[]') && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: dayjs().diff(startDate, 'day') * columnWidth,
                  width: 2,
                  bgcolor: 'error.main',
                  zIndex: 3,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -4,
                    left: -4,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: 'error.main',
                  },
                }}
              />
            )}

            {/* Dependency Arrows (SVG) - Flowchart Style */}
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: dateRange.length * columnWidth,
                height: taskHierarchy.length * ROW_HEIGHT,
                pointerEvents: 'none',
                zIndex: 1,
                overflow: 'visible',
              }}
            >
              <defs>
                {/* Arrowhead for completed dependencies */}
                <marker
                  id="arrowhead"
                  markerWidth="12"
                  markerHeight="8"
                  refX="11"
                  refY="4"
                  orient="auto"
                >
                  <path d="M 0 0 L 12 4 L 0 8 L 3 4 Z" fill="#6366f1" />
                </marker>
                {/* Arrowhead for blocked dependencies */}
                <marker
                  id="arrowhead-blocked"
                  markerWidth="12"
                  markerHeight="8"
                  refX="11"
                  refY="4"
                  orient="auto"
                >
                  <path d="M 0 0 L 12 4 L 0 8 L 3 4 Z" fill="#f59e0b" />
                </marker>
                {/* Arrowhead for completed (green) */}
                <marker
                  id="arrowhead-complete"
                  markerWidth="12"
                  markerHeight="8"
                  refX="11"
                  refY="4"
                  orient="auto"
                >
                  <path d="M 0 0 L 12 4 L 0 8 L 3 4 Z" fill="#16a34a" />
                </marker>
                {/* Drop shadow filter for connector lines */}
                <filter id="connector-shadow" x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodColor="#000" floodOpacity="0.15" />
                </filter>
              </defs>

              {getDependencyArrows.map((arrow) => {
                const isBlocked =
                  arrow.toTask.status !== TaskStatus.COMPLETED &&
                  arrow.fromTask.status !== TaskStatus.COMPLETED;
                const isComplete = 
                  arrow.fromTask.status === TaskStatus.COMPLETED &&
                  arrow.toTask.status === TaskStatus.COMPLETED;
                
                // Color scheme
                const strokeColor = isComplete ? '#16a34a' : isBlocked ? '#f59e0b' : '#6366f1';
                const markerEnd = isComplete 
                  ? 'url(#arrowhead-complete)' 
                  : isBlocked 
                  ? 'url(#arrowhead-blocked)' 
                  : 'url(#arrowhead)';

                return (
                  <g key={arrow.id}>
                    {/* Outer glow/shadow for visibility */}
                    <path
                      d={arrow.path}
                      fill="none"
                      stroke="white"
                      strokeWidth={5}
                      opacity={0.5}
                    />
                    {/* Main path line */}
                    <path
                      d={arrow.path}
                      fill="none"
                      stroke={strokeColor}
                      strokeWidth={2.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeDasharray={isBlocked ? '6,3' : isComplete ? '0' : '0'}
                      markerEnd={markerEnd}
                      filter="url(#connector-shadow)"
                      opacity={0.85}
                    />
                    {/* Start point circle (connector dot) */}
                    <circle
                      cx={arrow.fromX - 15}
                      cy={arrow.fromY}
                      r={4}
                      fill={strokeColor}
                      stroke="white"
                      strokeWidth={1.5}
                    />
                    {/* End point circle (before arrowhead) - only if not same row */}
                    {arrow.type !== 'horizontal' && (
                      <circle
                        cx={arrow.toX + 18}
                        cy={arrow.toY}
                        r={3}
                        fill={strokeColor}
                        stroke="white"
                        strokeWidth={1}
                        opacity={0.7}
                      />
                    )}
                  </g>
                );
              })}
            </svg>

            {/* Task Bars */}
            {taskHierarchy.map(({ task, level }, index) => {
              const position = getBarPosition(task);
              if (!position) return null;

              const isOverdue =
                dayjs(task.dueDate).isBefore(dayjs()) &&
                task.status !== TaskStatus.COMPLETED;
              const isBlocked = (task.blockedByTaskIds?.length || 0) > 0;
              const hasSubtasks = tasks.some((t) => t.parentTaskId === task.id);

              return (
                <Box
                  key={task.id}
                  sx={{
                    height: ROW_HEIGHT,
                    position: 'relative',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {/* Task Bar */}
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {task.title}
                        </Typography>
                        <Typography variant="caption">
                          {dayjs(task.startDate || task.dueDate).format('MMM D')} -{' '}
                          {dayjs(task.dueDate).format('MMM D, YYYY')}
                        </Typography>
                        <br />
                        <Typography variant="caption">Status: {task.status}</Typography>
                        {task.category && (
                          <>
                            <br />
                            <Typography variant="caption">
                              Category: {task.category}
                            </Typography>
                          </>
                        )}
                        {task.issueType && (
                          <>
                            <br />
                            <Typography variant="caption">
                              Type: {issueTypeConfig[task.issueType].label}
                            </Typography>
                          </>
                        )}
                        {isBlocked && (
                          <>
                            <br />
                            <Typography variant="caption" color="warning.main">
                              Blocked by {task.blockedByTaskIds?.length} task(s)
                            </Typography>
                          </>
                        )}
                      </Box>
                    }
                    arrow
                  >
                    <Box
                      onClick={() => handleTaskClick(task.id)}
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: position.left,
                        width: position.width,
                        height: 24,
                        bgcolor: isBlocked
                          ? alpha('#f59e0b', 0.3)
                          : `${statusColors[task.status]}30`,
                        border: '1px solid',
                        borderColor: isBlocked ? '#f59e0b' : statusColors[task.status],
                        borderRadius: 1,
                        cursor: 'pointer',
                        overflow: 'hidden',
                        transition: 'all 0.2s',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 0.5,
                        '&:hover': {
                          transform: 'scaleY(1.1)',
                          boxShadow: `0 2px 8px ${statusColors[task.status]}40`,
                        },
                      }}
                    >
                      {/* Task icon on bar */}
                      {position.width > 30 &&
                        (task.issueType && issueTypeConfig[task.issueType] ? (
                          React.createElement(issueTypeConfig[task.issueType].icon, {
                            size: 12,
                            style: { color: issueTypeConfig[task.issueType].color },
                          })
                        ) : hasSubtasks ? (
                          <Layers size={12} style={{ color: '#8b5cf6' }} />
                        ) : null)}

                      {/* Label */}
                      {position.width > 80 && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            color: 'text.primary',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flex: 1,
                            fontSize: '0.65rem',
                          }}
                        >
                          {task.title}
                        </Typography>
                      )}

                      {/* Overdue indicator */}
                      {isOverdue && position.width > 40 && (
                        <AlertTriangle size={10} style={{ color: '#ef4444' }} />
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              );
            })}

            {/* Milestones */}
            {milestones.map((milestone) => {
              const milestoneLeft = getMilestonePosition(milestone);
              if (milestoneLeft === null) return null;

              return (
                <Tooltip
                  key={milestone.id}
                  title={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {milestone.name}
                      </Typography>
                      <Typography variant="caption">
                        {dayjs(milestone.dueDate).format('MMM D, YYYY')}
                      </Typography>
                      {milestone.isCompleted && (
                        <Typography variant="caption" color="success.main">
                          {' '}
                          ✓ Completed
                        </Typography>
                      )}
                    </Box>
                  }
                  arrow
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: milestoneLeft,
                      width: 2,
                      bgcolor: milestone.isCompleted ? '#16a34a' : '#9333ea',
                      zIndex: 2,
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: -6,
                        left: -5,
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderTop: `10px solid ${milestone.isCompleted ? '#16a34a' : '#9333ea'}`,
                      },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Summary */}
      <Box
        sx={{
          p: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Chip
          label={`${tasks.length} Total Tasks`}
          size="small"
          variant="outlined"
        />
        <Chip
          label={`${tasks.filter((t) => t.status === TaskStatus.COMPLETED).length} Completed`}
          size="small"
          sx={{ bgcolor: alpha('#16a34a', 0.1), color: '#16a34a' }}
        />
        <Chip
          label={`${tasks.filter((t) => t.status === TaskStatus.IN_PROGRESS).length} In Progress`}
          size="small"
          sx={{ bgcolor: alpha('#2563eb', 0.1), color: '#2563eb' }}
        />
        <Chip
          label={`${tasks.filter((t) => (t.blockedByTaskIds?.length || 0) > 0).length} Blocked`}
          size="small"
          sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b' }}
        />
        <Chip
          label={`${getDependencyArrows.length} Dependencies`}
          size="small"
          sx={{ bgcolor: alpha('#6366f1', 0.1), color: '#6366f1' }}
        />
      </Box>
    </Card>
  );
};

export default ProjectTasksGanttChart;
